import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { Customer } from '../types';
import { supabase } from '@/integrations/supabase/client';

type Option = { value: string; label: string };

export const useCustomerFilterBuilder = (customers: Customer[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-customers-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-customers-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    leadSources: Option[];
    leadOwners: Option[];
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
    invoiceStatuses: Option[];
    estimateStatuses: Option[];
    projectStatuses: Option[];
  }>({ 
    leadSources: [], 
    leadOwners: [], 
    cities: [], 
    provinces: [], 
    suburbs: [], 
    tags: [],
    invoiceStatuses: [],
    estimateStatuses: [],
    projectStatuses: []
  });

  // Fetch comprehensive filter options from database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch customers and profiles in parallel
        const [customersRes, profilesRes] = await Promise.all([
          supabase.from('customers').select('lead_source, suburb, city, province_state, tags, lead_owner_id'),
          supabase.from('profiles').select('id, first_name, last_name')
        ]);

        if (customersRes.error) throw customersRes.error;
        if (profilesRes.error) throw profilesRes.error;

        const customers = customersRes.data || [];
        const profiles = profilesRes.data || [];

        // Extract unique options from customers
        const leadSourcesSet = new Set<string>();
        const suburbsSet = new Set<string>();
        const citiesSet = new Set<string>();
        const provincesSet = new Set<string>();
        const tagsSet = new Set<string>();
        const ownerIdsSet = new Set<string>();

        customers.forEach((c: any) => {
          if (c.lead_source) leadSourcesSet.add(c.lead_source);
          if (c.suburb) suburbsSet.add(c.suburb);
          if (c.city) citiesSet.add(c.city);
          if (c.province_state) provincesSet.add(c.province_state);
          if (Array.isArray(c.tags)) c.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
          if (c.lead_owner_id) ownerIdsSet.add(c.lead_owner_id);
        });

        // Create lead owner options and sort alphabetically
        const leadOwnerOptions: Option[] = profiles
          .filter(p => ownerIdsSet.has(p.id))
          .map(p => ({
            value: p.id,
            label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown'
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setOptionsOverride({
          leadSources: Array.from(leadSourcesSet).map(s => ({ value: s, label: s })).sort((a, b) => a.label.localeCompare(b.label)),
          leadOwners: leadOwnerOptions,
          cities: Array.from(citiesSet).map(c => ({ value: c, label: c })).sort((a, b) => a.label.localeCompare(b.label)),
          provinces: Array.from(provincesSet).map(p => ({ value: p, label: p })).sort((a, b) => a.label.localeCompare(b.label)),
          suburbs: Array.from(suburbsSet).map(s => ({ value: s, label: s })).sort((a, b) => a.label.localeCompare(b.label)),
          tags: Array.from(tagsSet).map(t => ({ value: t, label: t })).sort((a, b) => a.label.localeCompare(b.label)),
          invoiceStatuses: [
            { value: 'draft', label: 'Draft' },
            { value: 'sent', label: 'Sent' },
            { value: 'paid', label: 'Paid' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'cancelled', label: 'Cancelled' }
          ],
          estimateStatuses: [
            { value: 'draft', label: 'Draft' },
            { value: 'sent', label: 'Sent' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'expired', label: 'Expired' }
          ],
          projectStatuses: [
            { value: 'planning', label: 'Planning' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' },
            { value: 'on_hold', label: 'On Hold' }
          ]
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchOptions();
  }, []);

  // Generate dynamic options from current customers data
  const dynamicOptions = useMemo(() => {
    const leadSources = new Set<string>();
    const cities = new Set<string>();
    const provinces = new Set<string>();
    const suburbs = new Set<string>();
    const tags = new Set<string>();

    customers.forEach(customer => {
      if (customer.lead_source) leadSources.add(customer.lead_source);
      if (customer.city) cities.add(customer.city);
      if (customer.province_state) provinces.add(customer.province_state);
      if (customer.suburb) suburbs.add(customer.suburb);
      if (Array.isArray(customer.tags)) {
        customer.tags.forEach(tag => tags.add(tag));
      }
    });

    return {
      leadSources: Array.from(leadSources).map(s => ({ value: s, label: s })).sort((a, b) => a.label.localeCompare(b.label)),
      cities: Array.from(cities).map(c => ({ value: c, label: c })).sort((a, b) => a.label.localeCompare(b.label)),
      provinces: Array.from(provinces).map(p => ({ value: p, label: p })).sort((a, b) => a.label.localeCompare(b.label)),
      suburbs: Array.from(suburbs).map(s => ({ value: s, label: s })).sort((a, b) => a.label.localeCompare(b.label)),
      tags: Array.from(tags).map(t => ({ value: t, label: t })).sort((a, b) => a.label.localeCompare(b.label))
    };
  }, [customers]);

  // Combine override options with dynamic options, prioritizing override
  const finalOptions = useMemo(() => ({
    leadSources: optionsOverride.leadSources.length > 0 ? optionsOverride.leadSources : dynamicOptions.leadSources,
    leadOwners: optionsOverride.leadOwners,
    cities: optionsOverride.cities.length > 0 ? optionsOverride.cities : dynamicOptions.cities,
    provinces: optionsOverride.provinces.length > 0 ? optionsOverride.provinces : dynamicOptions.provinces,
    suburbs: optionsOverride.suburbs.length > 0 ? optionsOverride.suburbs : dynamicOptions.suburbs,
    tags: optionsOverride.tags.length > 0 ? optionsOverride.tags : dynamicOptions.tags,
    invoiceStatuses: optionsOverride.invoiceStatuses,
    estimateStatuses: optionsOverride.estimateStatuses,
    projectStatuses: optionsOverride.projectStatuses
  }), [optionsOverride, dynamicOptions]);

  // Define filter entities
  const filterEntities: FilterEntity[] = [
    { key: 'customer', label: 'Customer' },
    { key: 'invoice', label: "Customer's Invoice" },
    { key: 'estimate', label: "Customer's Estimate" },
    { key: 'project', label: "Customer's Project" }
  ];

  // Define attributes by entity
  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    customer: [
      { key: 'customer_type', label: "'s Type", type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' }
      ]},
      { key: 'name', label: "'s Name", type: 'text' },
      { key: 'last_name', label: "'s Last Name", type: 'text' },
      { key: 'emails', label: "'s Email", type: 'text' },
      { key: 'cell_numbers', label: "'s Phone", type: 'text' },
      { key: 'lead_source', label: "'s Lead Source", type: 'select', options: finalOptions.leadSources },
      { key: 'lead_owner_id', label: "'s Lead Owner", type: 'select', options: finalOptions.leadOwners },
      { key: 'tags', label: "'s Tags", type: 'select', options: finalOptions.tags },
      { key: 'street_address', label: "'s Street Address", type: 'text' },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: finalOptions.suburbs },
      { key: 'city', label: "'s City", type: 'select', options: finalOptions.cities },
      { key: 'province_state', label: "'s Province/State", type: 'select', options: finalOptions.provinces },
      { key: 'postal_code', label: "'s Postal Code", type: 'text' },
      { key: 'created_at', label: "'s Date Added", type: 'date' },
      { key: 'updated_at', label: "'s Last Modified Date", type: 'date' }
    ],
    invoice: [
      { key: 'status', label: "'s Status", type: 'select', options: finalOptions.invoiceStatuses },
      { key: 'total_amount', label: "'s Total Amount", type: 'number' },
      { key: 'invoice_number', label: "'s Invoice Number", type: 'text' },
      { key: 'created_at', label: "'s Date Created", type: 'date' },
      { key: 'due_date', label: "'s Due Date", type: 'date' }
    ],
    estimate: [
      { key: 'status', label: "'s Status", type: 'select', options: finalOptions.estimateStatuses },
      { key: 'estimate_number', label: "'s Estimate Number", type: 'text' },
      { key: 'title', label: "'s Title", type: 'text' },
      { key: 'created_at', label: "'s Date Created", type: 'date' },
      { key: 'expires_at', label: "'s Expires At", type: 'date' }
    ],
    project: [
      { key: 'status', label: "'s Status", type: 'select', options: finalOptions.projectStatuses },
      { key: 'name', label: "'s Project Name", type: 'text' },
      { key: 'created_at', label: "'s Date Created", type: 'date' }
    ]
  };

  // Generate filter fields
  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [];
    
    filterEntities.forEach(entity => {
      const attributes = filterAttributesByEntity[entity.key] || [];
      attributes.forEach(attribute => {
        fields.push({
          key: `${entity.key}.${attribute.key}`,
          label: `${entity.label} - ${attribute.label}`,
          type: attribute.type,
          options: attribute.options
        });
      });
    });

    return fields;
  }, [filterEntities, filterAttributesByEntity]);

  // Filter management functions
  const addGroup = useCallback(() => {
    const newRule: FilterRule = {
      id: `rule-${Date.now()}`,
      entity: '',
      attribute: '',
      operator: 'equals',
      value: ''
    };

    const newGroup: FilterGroup = {
      id: `group-${Date.now()}`,
      rules: [newRule],
      logic: 'AND'
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, []);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: `rule-${Date.now()}`,
      entity: '',
      attribute: '',
      operator: 'equals',
      value: ''
    };

    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: [...group.rules, newRule] }
        : group
    ));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            rules: group.rules.map(rule => 
              rule.id === ruleId ? { ...rule, ...updates } : rule
            )
          }
        : group
    ));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: group.rules.filter(rule => rule.id !== ruleId) }
        : group
    ));
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  // Check if rule is complete
  const isRuleComplete = useCallback((rule: FilterRule): boolean => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    if (['is_empty', 'is_not_empty'].includes(rule.operator)) return true;
    if (rule.operator === 'between') {
      return Array.isArray(rule.value) && rule.value.length === 2 && 
             rule.value[0] !== undefined && rule.value[1] !== undefined;
    }
    return rule.value !== undefined && rule.value !== '';
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedGroups([...draftGroups]);
  }, [draftGroups]);

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  }, [draftGroups, appliedGroups]);

  const canApplyFilters = useMemo(() => {
    return draftGroups.some(group => 
      group.rules.some(rule => isRuleComplete(rule))
    );
  }, [draftGroups, isRuleComplete]);

  // Get field value from customer based on entity and attribute
  const getFieldValue = useCallback((customer: Customer, entity: string, attribute: string): any => {
    if (entity === 'customer') {
      return (customer as any)[attribute];
    }
    // For related entities (invoice, estimate, project), we would need to fetch related data
    // For now, return undefined to indicate unsupported
    return undefined;
  }, []);

  // Evaluate a single rule against a customer
  const evaluateRule = useCallback((customer: Customer, rule: FilterRule): boolean => {
    if (!isRuleComplete(rule)) return true;

    const fieldValue = getFieldValue(customer, rule.entity, rule.attribute);
    const { operator, value } = rule;

    // Handle different operators
    switch (operator) {
      case 'equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => value.includes(v));
          }
          return value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(value);
        }
        return fieldValue === value;

      case 'not_equals':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => value.includes(v));
          }
          return !value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return !fieldValue.includes(value);
        }
        return fieldValue !== value;

      case 'contains':
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(item => 
            String(item).toLowerCase().includes(String(value).toLowerCase())
          );
        }
        return String(fieldValue || '').toLowerCase().includes(String(value).toLowerCase());

      case 'not_contains':
        if (Array.isArray(fieldValue)) {
          return !fieldValue.some(item => 
            String(item).toLowerCase().includes(String(value).toLowerCase())
          );
        }
        return !String(fieldValue || '').toLowerCase().includes(String(value).toLowerCase());

      case 'greater_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('at')) {
          return new Date(fieldValue) > new Date(value);
        }
        return Number(fieldValue) > Number(value);

      case 'less_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('at')) {
          return new Date(fieldValue) < new Date(value);
        }
        return Number(fieldValue) < Number(value);

      case 'between':
        if (Array.isArray(value) && value.length === 2) {
          if (rule.attribute.includes('date') || rule.attribute.includes('at')) {
            const date = new Date(fieldValue);
            return date >= new Date(value[0]) && date <= new Date(value[1]);
          }
          const num = Number(fieldValue);
          return num >= Number(value[0]) && num <= Number(value[1]);
        }
        return false;

      case 'is_empty':
        return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);

      case 'is_not_empty':
        return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);

      case 'in':
        return Array.isArray(value) && value.includes(fieldValue);

      case 'not_in':
        return Array.isArray(value) && !value.includes(fieldValue);

      default:
        return true;
    }
  }, [getFieldValue, isRuleComplete]);

  // Apply filters to customers
  const filteredCustomers = useMemo(() => {
    if (appliedGroups.length === 0) return customers;

    return customers.filter(customer => {
      // All groups must pass (AND logic between groups)
      return appliedGroups.every(group => {
        if (group.rules.length === 0) return true;

        // Within each group, apply the group's logic (AND/OR)
        if (group.logic === 'AND') {
          return group.rules.every(rule => evaluateRule(customer, rule));
        } else {
          return group.rules.some(rule => evaluateRule(customer, rule));
        }
      });
    });
  }, [customers, appliedGroups, evaluateRule]);

  const hasActiveFilters = appliedGroups.length > 0 && 
    appliedGroups.some(group => group.rules.length > 0);

  return {
    // State
    draftGroups,
    appliedGroups,
    filteredCustomers,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,

    // Actions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,

    // Configuration
    filterFields,
    filterEntities,
    filterAttributesByEntity
  };
};
